import _extends from "@babel/runtime/helpers/extends";
import React from "react";
import Text from "~/components-shared/Text";
import Card from "~/components-shared/Card/Card";
import Button from "~/components-shared/Button";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import Icon from "~/components-shared/Icon";
import { CRUDActionEnum, AuthoritiesEnum, RoutesEnum, StatusStateEnum, IntegrationTypeEnum } from "~/types/enums";
import { useHistory } from "react-router-dom";
import moment from "moment";
import Picture from "~/components-shared/Picture";
import { useDispatch, useSelector } from "react-redux";
import authSelectors from "~/store/auth/selectors";
import { Dropdown } from "~/components-shared";
import communitiesActionCreators from "~/store/communities/actions";
import useAbacExt from "~/hooks/abac";
import communitySelectors from "~/store/communities/selectors";
import integrationSelectors from "~/store/integrations/selectors";
import { getSlackSpaceUrl } from "~/lib/helpers";
const StyledText = styled(Text)`
  flex-grow: 1;
  line-height: 1.4em;
`;
const StyledButton = styled(Button)`
  position: absolute;
  left: 0.5em;
  top: 0.5em;
`;
const Options = styled.div`
  position: absolute;
  right: 0.5em;
  top: 0.5em;
  display: flex;
  align-items: center;
  min-height: 32px;
`;
const CommunityCard = _ref => {
  let {
    children,
    community,
    onClick,
    ...rest
  } = _ref;
  const {
    colors
  } = useTheme();
  const {
    t
  } = useTranslation();
  const history = useHistory();
  const user = useSelector(authSelectors.getUser);
  const communityStatus = useSelector(communitySelectors.getStatusByCommunity(community.id));
  const dispatch = useDispatch();
  const {
    userHas,
    userHasOneOf
  } = useAbacExt();
  const options = [];
  const slackIntegration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.SLACK));
  const handleView = () => {
    if (typeof onClick === "function") {
      onClick(community);
    } else {
      history.push(RoutesEnum.COMMUNITY.replace(":communityId", community.id));
    }
  };
  const handleJoinCommunity = e => {
    e.stopPropagation();
    dispatch(communitiesActionCreators.joinStarted(community.id));
  };
  const handleLeaveCommunity = e => {
    e.stopPropagation();
    dispatch(communitiesActionCreators.leaveStarted(community.id));
  };
  const handleMessage = e => {
    e.stopPropagation();
    if (slackIntegration && community.slackId) {
      window.open(`${getSlackSpaceUrl(slackIntegration)}/archives/${community.slackId}`, "_blank");
    } else {
      window.open(`mailto:${community.email}`, "_blank");
    }
  };
  if (userHasOneOf([AuthoritiesEnum.COMMUNITIES_WRITE], community.createdBy?.id)) {
    options.push({
      id: 1,
      name: t("common.actions.edit"),
      icon: "pencil",
      action: e => {
        e.stopPropagation();
        dispatch(communitiesActionCreators.actionChanged(CRUDActionEnum.UPDATE, community.id));
      }
    });
  }
  if (userHasOneOf([AuthoritiesEnum.COMMUNITIES_WRITE], community.createdBy?.id)) {
    options.push({
      id: 2,
      name: /*#__PURE__*/React.createElement(Text, null, t("common.actions.delete")),
      icon: "bin",
      action: e => {
        e.stopPropagation();
        dispatch(communitiesActionCreators.actionChanged(CRUDActionEnum.DELETE, community.id));
      }
    });
  }
  return /*#__PURE__*/React.createElement(Card, _extends({
    shadow: true,
    align: "center",
    onClick: handleView,
    minHeight: "31rem"
  }, rest), userHas(AuthoritiesEnum.COMMUNITIES_WRITE) && (community.memberIds.includes(user?.id || "") ? /*#__PURE__*/React.createElement(StyledButton, {
    rounded: true,
    fontSize: 3.5,
    s: {
      m: 0,
      py: 2,
      px: 3
    },
    color: "transparent",
    display: "inline-block",
    onClick: handleLeaveCommunity,
    loading: communityStatus === StatusStateEnum.LOADING
  }, t("common.actions.leave")) : /*#__PURE__*/React.createElement(StyledButton, {
    rounded: true,
    fontSize: 3.5,
    s: {
      m: 0,
      py: 2,
      px: 3
    },
    color: "primaryTransparent",
    display: "inline-block",
    onClick: handleJoinCommunity,
    loading: communityStatus === StatusStateEnum.LOADING
  }, "+ ", t("common.actions.join"))), /*#__PURE__*/React.createElement(Picture, {
    src: community.picture,
    name: community.name,
    color: community.color || colors.secondary.main,
    width: "10rem",
    height: "10rem",
    s: {
      mt: 12,
      mb: 3
    }
  }), /*#__PURE__*/React.createElement(Options, null, /*#__PURE__*/React.createElement(Icon, {
    src: "people",
    color: colors.text.primary,
    scale: 3,
    s: {
      mr: 1
    }
  }), " ", community.memberIds.length, " ", /*#__PURE__*/React.createElement(Text, {
    element: "span",
    color: colors.secondary.main,
    s: {
      mx: 1
    }
  }, "(", community.activeMemberIds.length, ")"), !!options.length && /*#__PURE__*/React.createElement(Dropdown, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("common.actions.options"),
    display: "inline-block",
    direction: "bottomLeft",
    handlerProps: {
      rounded: true,
      s: {
        p: 2
      }
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      src: "dots-vertical",
      display: "block",
      color: colors.text.primary
    })
  }, _ref2 => {
    let {
      isOpen,
      onItemClick
    } = _ref2;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, options.map(option => /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        onItemClick();
        if (option.action) option.action(e);
      },
      key: option.id
    }, /*#__PURE__*/React.createElement(Icon, {
      src: option.icon,
      s: {
        mr: 2
      },
      color: "primary"
    }), option.name)));
  })), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Text, {
    element: "h2"
  }, community.name), /*#__PURE__*/React.createElement(Text, {
    element: "p",
    color: "textSecondary",
    fontSize: 3.5,
    s: {
      mb: 3
    }
  }, t("CommunityCard.founded"), " ", moment(community.founded).format("MMM YYYY"))), /*#__PURE__*/React.createElement(StyledText, {
    element: "span",
    color: "textSecondary",
    s: {
      mb: 3
    }
  }, community.mission), /*#__PURE__*/React.createElement("div", null, (slackIntegration && community.slackName || community.email) && /*#__PURE__*/React.createElement(Button, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("CommunityCard.writeMessage"),
    color: "transparent",
    onClick: handleMessage,
    s: {
      mb: 2,
      py: 2,
      px: 4
    },
    rounded: true,
    fontSize: 3.5
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "message",
    color: colors.text.primary,
    s: {
      mr: 1
    }
  }), slackIntegration && community.slackId ? `#${community.slackName}` : community.email)));
};
export default CommunityCard;